import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIsEmbeddedWebView } from '@hungryroot/environment';
import { buildClientForceVariantEnv } from '../client-side-enrollment/buildClientForceVariantEnv';
import { getExperimentEnrollment } from '../client-side-enrollment/enroll';
import { type ExperimentVariants, type Experiments } from '../experiments';
import { type ExperimentHookOptions } from '../types';

export const COOKIE_NAME = 'experiment_enrollments';

/*
  This hook enrolls or returns a user's experiment variant for
  PRE SIGN UP experiments only.

  This hook can return one of 3 values:
    - ExperimentVariants<T> where T is one of the experiment numbers we care about
    - null
    - undefined

  When this hook returns "null" it means that we cannot properly enroll the user
  because either the experiment definition is missing entirely, or its malformed
  or is not active.

  "null" isn't terribly usefull for our components, so there will need to be a downstream
  hook that translates that to the control version of the experiment

  When this hook returns "undefined" it means that we are still trying to resolve enrollment

  When the hook emits ExperimentVariants<T>, that means that we know how the user is enrolled
  and you have the variant.

  When the variant is read from cookies, this hook will synchronously emit that variant value.
  Otherwise, it will emit undefined first and eventually one of ExperimentVariants<T> or null.

*/
export function useClientSidePresignupExperiment<T extends Experiments>(
  {
    exp,
  }: {
    exp: T;
  },
  { enabled }: Pick<ExperimentHookOptions, 'enabled'> = {}
) {
  const isEmbeddedWebView = useIsEmbeddedWebView();
  const queryClient = useQueryClient();
  const enrollmentState = useMemo(() => {
    if (enabled === false) {
      return undefined;
    }

    return getExperimentEnrollment({
      expId: exp,
      queryClient,
      env: buildClientForceVariantEnv({ isEmbeddedWebView }),
    });
  }, [enabled, isEmbeddedWebView, exp, queryClient]);

  const [variant, setVariant] = useState<
    ExperimentVariants<T> | null | undefined
  >(() => {
    if (enrollmentState === undefined) {
      return undefined;
    }

    if (enrollmentState.variant !== undefined) {
      return enrollmentState.variant;
    }

    return undefined;
  });

  if (variant === undefined && enrollmentState?.variant !== undefined) {
    setVariant(enrollmentState.variant);
  }

  useEffect(() => {
    if (enrollmentState?.state !== 'not-enrolled') {
      return;
    }

    enrollmentState.enroll().then((variant) => {
      setVariant(variant);
    });
  }, [enrollmentState, queryClient]);

  if (typeof variant === 'number') {
    return (variant % 10) as ExperimentVariants<T>;
  }

  return variant;
}
